import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';
import { RefreshCw, Search } from 'lucide-react';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid, applyFilters } from '@/components/data';
import { useIsMobile } from '@/hooks/useIsMobile';
import PhonePayments from './Payments/PhonePayments';

/**
 * What is owed, and what has just come in — on one screen.
 *
 * These were two tabs. They are the two halves of one question: an accountant
 * looking at a customer's balance wants to know whether the cheque that landed
 * this morning was theirs, and tabbing between the two to find out is the whole
 * friction. Outstanding is the working list; recent payments run down the rail
 * beside it.
 *
 * The outstanding table also gains what it never had: a totals row. A list of
 * money without a total is a list, not a report.
 *
 * On a phone the two halves become two tabs over the same data — the rows,
 * the filter and the sort are all decided here, so both screens agree.
 */

const money = (v) => `₹${(v || 0).toLocaleString('en-IN')}`;

const STATUS_TONE = {
  fully_paid: 'good',
  partially_paid: 'warn',
  unpaid: 'danger',
};

export default function PaymentsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [overview, setOverview] = useState(null);
  const [outstanding, setOutstanding] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  // which of the two lists the phone is showing; the desktop shows both
  const [view, setView] = useState('owing');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, outstandingRes, recentRes] = await Promise.all([
        api.get('/payments/overview'),
        api.get('/payments/outstanding'),
        api.get('/payments/recent'),
      ]);
      setOverview(overviewRes.data);
      setOutstanding(outstandingRes.data.customers);
      setRecent(recentRes.data.payments);
    } catch { toast.error('Failed to load payments data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = useMemo(() => ([
    {
      key: 'customer_name',
      header: 'Customer',
      width: 'minmax(14rem,1.6fr)',
      filter: true,
      sortable: true,
      filterValue: (c) => `${c.customer_name || ''} ${c.firm_name || ''}`,
      render: (c) => (
        <>
          <span className="block truncate font-semibold text-slate-900">{c.customer_name}</span>
          {c.firm_name && <span className="block truncate text-[11px] leading-tight text-slate-400">{c.firm_name}</span>}
        </>
      ),
    },
    {
      key: 'total_value',
      header: 'Total Value',
      align: 'right',
      width: '9rem',
      sortable: true,
      render: (c) => <span className="text-slate-600">{money(c.total_value)}</span>,
      total: (rows) => money(rows.reduce((s, c) => s + (c.total_value || 0), 0)),
    },
    {
      key: 'total_paid',
      header: 'Paid',
      align: 'right',
      width: '9rem',
      sortable: true,
      render: (c) => <span className="text-emerald-600">{money(c.total_paid)}</span>,
      total: (rows) => money(rows.reduce((s, c) => s + (c.total_paid || 0), 0)),
    },
    {
      key: 'outstanding',
      header: 'Outstanding',
      align: 'right',
      width: '9rem',
      sortable: true,
      render: (c) => <span className="font-bold text-red-600">{money(c.outstanding)}</span>,
      total: (rows) => money(rows.reduce((s, c) => s + (c.outstanding || 0), 0)),
    },
    {
      key: 'order_count',
      header: 'Orders',
      align: 'right',
      width: '6rem',
      sortable: true,
      render: (c) => <span className="text-slate-600">{c.order_count}</span>,
      total: (rows) => rows.reduce((s, c) => s + (c.order_count || 0), 0),
    },
  ]), []);

  const [sort, setSort] = useState({ key: 'outstanding', dir: 'desc' });

  const visibleRows = useMemo(() => {
    let rows = applyFilters(outstanding, columns, filters);
    if (sort?.key) {
      rows = [...rows].sort((a, b) => {
        const av = a[sort.key], bv = b[sort.key];
        const cmp = typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
        return sort.dir === 'desc' ? -cmp : cmp;
      });
    }
    return rows;
  }, [outstanding, columns, filters, sort]);

  const filteredRecent = useMemo(() => {
    if (!search) return recent;
    const q = search.toLowerCase();
    return recent.filter(p =>
      p.order_id?.toLowerCase().includes(q) ||
      p.customer_name?.toLowerCase().includes(q) ||
      p.firm_name?.toLowerCase().includes(q));
  }, [recent, search]);

  const stats = [
    { key: null, label: 'Outstanding', value: money(overview?.total_outstanding), tone: 'danger' },
    { key: null, label: 'Billed', value: money(overview?.total_billed), tone: 'blue' },
    { key: null, label: 'This month', value: money(overview?.collections_this_month), tone: 'good' },
    { key: null, label: 'This week', value: money(overview?.collections_this_week) },
    ...Object.entries(overview?.status_breakdown || {}).map(([status, count]) => ({
      key: null,
      label: status.replace('_', ' '),
      value: count,
      tone: STATUS_TONE[status],
    })),
  ];

  if (isMobile) {
    return (
      <PhonePayments
        loading={loading}
        overview={overview}
        owingCount={outstanding.length}
        outstanding={visibleRows}
        recent={filteredRecent}
        totals={{
          outstanding: visibleRows.reduce((s, c) => s + (c.outstanding || 0), 0),
          paid: visibleRows.reduce((s, c) => s + (c.total_paid || 0), 0),
          value: visibleRows.reduce((s, c) => s + (c.total_value || 0), 0),
        }}
        view={view}
        onView={setView}
        onSearchOwing={(text) => setFilters(f => ({ ...f, customer_name: text }))}
        onSearchRecent={setSearch}
        onRefresh={fetchData}
        onOpenOrder={(p) => p.order_uuid && navigate(`/orders/${p.order_uuid}`)}
        onOpenCustomer={(c) => navigate(`/customers/${c._id}`)}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
      </div>
    );
  }

  return (
    <Screen data-testid="payments-page">
      <CommandBar
        title="Payments"
        subtitle={`${outstanding.length} customer${outstanding.length === 1 ? '' : 's'} owing`}
        actions={[{ key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchData }]}
      />

      <StatStrip stats={stats} data-testid="payment-stats" />

      <ScreenSplit
        railWidth="22rem"
        rail={
          <Pane
            title="Recent payments"
            aside={`${filteredRecent.length}`}
            className="max-h-full"
            actions={
              <span className="relative">
                <Search className="pointer-events-none absolute left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-white/60" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="find…"
                  aria-label="Search recent payments"
                  className="h-5 w-28 border-white/30 bg-white/10 pl-5 text-[11px] text-white placeholder:text-white/50"
                />
              </span>
            }
          >
            {filteredRecent.length === 0 ? (
              <p className="p-4 text-center text-xs text-slate-400">No recent payments</p>
            ) : filteredRecent.map((p, i) => (
              <button
                key={i}
                type="button"
                onClick={() => p.order_uuid && navigate(`/orders/${p.order_uuid}`)}
                className="flex w-full items-start justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left transition-colors last:border-b-0 hover:bg-slate-50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-[12px] font-medium text-slate-700">{p.customer_name}</span>
                  <span className="block truncate text-[10px] text-slate-400">
                    {p.order_id}
                    {p.payment?.recorded_by_name ? ` · ${p.payment.recorded_by_name}` : ''}
                  </span>
                  <span className="block text-[10px] text-slate-400">
                    {p.payment?.date ? formatIST(p.payment.date, { compact: true }) : '—'}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-0.5">
                  <span className="text-[12px] font-bold tabular-nums text-emerald-600">
                    {money(p.payment?.amount)}
                  </span>
                  {p.payment?.mode && (
                    <Badge variant="secondary" className="px-1 py-0 text-[9px]">{p.payment.mode}</Badge>
                  )}
                </span>
              </button>
            ))}
          </Pane>
        }
      >
        <Pane
          title="Outstanding by customer"
          aside={`${visibleRows.length} of ${outstanding.length}`}
          scroll={false}
          bodyClassName="flex flex-col"
        >
          <DataGrid
            data-testid="outstanding-table"
            columns={columns}
            rows={visibleRows}
            getRowId={(c) => c._id}
            filters={filters}
            onFilterChange={(key, value) => setFilters(f => ({ ...f, [key]: value }))}
            sort={sort}
            onSortChange={setSort}
            showTotals={visibleRows.length > 0}
            empty="No outstanding payments"
          />
        </Pane>
      </ScreenSplit>
    </Screen>
  );
}
